import { User } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { firebaseStripeService } from '../firebase-stripe.service';

export interface UserInitializationData {
  email: string;
  displayName?: string;
  phoneNumber?: string;
  // Informations supplémentaires pour Stripe
  firstName?: string;
  lastName?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    postal_code?: string;
    country?: string;
  };
}

export const userInitializationService = {
  /**
   * 🔧 Initialise complètement un utilisateur avec toutes les informations nécessaires
   * pour éviter les problèmes de synchronisation Stripe
   */
  async initializeUser(userId: string, userData: UserInitializationData): Promise<void> {
    try {
      // 1. Créer/Mettre à jour le document customer Stripe AVEC l'email correct
      await firebaseStripeService.createCustomer(userId, userData.email);

      // 2. Mettre à jour le profil utilisateur avec les informations complètes
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        email: userData.email, // S'assurer que l'email est une string
        displayName: userData.displayName || '',
        phoneNumber: userData.phoneNumber || '',
        firstName: userData.firstName,
        lastName: userData.lastName,
        address: userData.address,
        stripeInitialized: true, // Flag pour indiquer que Stripe est initialisé
        updatedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Erreur lors de l\'initialisation utilisateur:', error);
      throw error;
    }
  },

  /**
   * 🔧 Initialise un utilisateur lors de l'onboarding
   */
  async initializeOnOnboarding(user: User, onboardingData: any): Promise<void> {
    if (!user.email) {
      throw new Error('Email utilisateur requis pour l\'initialisation');
    }

    const userData: UserInitializationData = {
      email: user.email,
      displayName: user.displayName || '',
      phoneNumber: user.phoneNumber || '',
      firstName: onboardingData?.partners?.partner1?.firstName || onboardingData?.firstName,
      lastName: onboardingData?.partners?.partner1?.lastName || onboardingData?.lastName,
      address: onboardingData?.address
    };

    await this.initializeUser(user.uid, userData);
  },

  /**
   * 🔧 Initialise un utilisateur lors de l'achat d'abonnement
   */
  async initializeOnSubscriptionPurchase(userId: string, email: string, customerData?: any): Promise<void> {
    const userData: UserInitializationData = {
      email: email,
      displayName: customerData?.name || '',
      phoneNumber: customerData?.phone || '',
      firstName: customerData?.firstName,
      lastName: customerData?.lastName,
      address: customerData?.address
    };

    await this.initializeUser(userId, userData);
  },

  /**
   * 🔧 Vérifie si un utilisateur est correctement initialisé
   */
  async checkUserInitialization(userId: string): Promise<boolean> {
    try {
      // Vérifier que le customer Stripe existe
      const customer = await firebaseStripeService.getCustomer(userId);
      
      if (!customer) {
        return false;
      }

      return true;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'initialisation utilisateur:', error);
      return false;
    }
  }
}; 